import { useEffect, useState } from "react";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import useDialer from "./hooks/useDialer";
import Loading from "../Goals/components/Loader";
import CallBar from "./components/CallBar";
import SinglePerson from "./components/SinglePerson";
import CallButton from "./components/CallButton";
import CallOutcome from "./components/CallOutcome";
import CallNotes from "./components/CallNotes";
import CALL_STATES from "./utils/states";
import PlatformMessage from "./components/PlatformMessage";

import {
  DIALER_NAME,
  checkValidLeads,
  createDialerSession,
  endCall,
  getPersonAndNumbers,
  logCall,
  startRecording,
  stopRecording,
  // non-async/actions
  actionCableUpdateReceived,
  setCallState,
  setPhoneNumber,
  clearSession,
  setCallOutcome,
  setComment,
  setCallParams,
  setDuration,
} from "../reducers/dialerReducer/dialerReducer";

import {
  useCallOutcome,
  useCallState,
  useConference,
  useError,
  useLeadUuids,
  useLoading,
  usePerson,
  usePhoneNumber,
  usePhoneNumbers,
  useSession,
  useValidLeads,
  useIsRecording,
  useComment,
  useIsRecordingConsentRequired,
} from "../reducers/dialerReducer/selectors";

import { DISABLED_CALL_STATES } from "./shared";
import RecordConfirmationModal from "./components/RecordConfirmationModal";

const CLOSABLE_STATES = [CALL_STATES.Ended, CALL_STATES.Init];

/* DO NOT USE THIS MODAL IN OTHER PAGES/COMPONENTS
  This modal is mounted in Header (global)
*/
const SingleDialerModal = () => {
  const leadUuids = useLeadUuids();

  const isRecordingConsentRequired = useIsRecordingConsentRequired();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const callOutcome = useCallOutcome();
  const callState = useCallState();
  const isRecording = useIsRecording();
  const conference = useConference();
  const error = useError();
  const loading = useLoading();
  const person = usePerson();
  const note = useComment();
  const phoneNumber = usePhoneNumber();
  const phoneNumbers = usePhoneNumbers();
  const session = useSession();
  const validLeads = useValidLeads();

  const strtRec = startRecording(DIALER_NAME);
  const stpRec = stopRecording(DIALER_NAME);

  useEffect(() => {
    if (isRecordingConsentRequired) setShowConsentModal(true);
  }, [isRecordingConsentRequired]);

  const { actions, disconnectDialer } = useDialer({
    actions: {
      checkValidLeads: checkValidLeads(DIALER_NAME),
      createDialerSession: createDialerSession(DIALER_NAME),
      endCall: endCall(DIALER_NAME),
      getPersonAndNumbers: getPersonAndNumbers(DIALER_NAME),
      logCall: logCall(DIALER_NAME),
      startRecording: strtRec,
      stopRecording: stpRec,
      actionCableUpdateReceived,
      setCallState,
      setPhoneNumber,
      clearSession,
      setCallOutcome,
      setComment,
      setCallParams,
    },
    selectors: {
      callOutcome,
      callState,
      conference,
      leadUuids,
      loading,
      person,
      phoneNumber,
      phoneNumbers,
      session,
      validLeads,
    },
  });

  const onClose = CLOSABLE_STATES.includes(callState)
    ? () => {
        disconnectDialer();
        actions.setCallParams();
      }
    : null;

  return (
    <Modal
      // Fighting with the Flyout z-index
      containerClassName="!tw-z-[3001]"
      className="tw-flex tw-items-center tw-justify-center"
      contentClassName="tw-max-w-[700px] tw-w-[100vw] tw-flex tw-flex-col tw-gap-[32px]"
      onHide={onClose}
      show={leadUuids?.length === 1}
    >
      <Modal.Header title="Call" closeButton={Boolean(onClose)} />
      <Modal.Body className="tw-flex tw-flex-col tw-gap-[32px]">
        <CallBar
          callState={callState}
          isRecording={isRecording}
          conference={conference}
          startRecording={strtRec}
          stopRecording={stpRec}
          setDuration={setDuration}
        />

        <PlatformMessage />

        <div className="tw-flex tw-justify-between tw-items-end">
          <SinglePerson
            loading={loading}
            callState={callState}
            person={person}
            phoneNumbers={phoneNumbers}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
          />
          <CallButton callState={callState} phoneNumber={phoneNumber} setCallState={setCallState} />
        </div>

        <CallOutcome callOutcome={callOutcome} setCallOutcome={setCallOutcome} />

        <CallNotes
          isDnc={phoneNumber?.meta.isDnc}
          mentionableIdOrUuid={person?.uuid}
          mentionableType="Person"
          note={note}
          setComment={setComment}
        />

        {error && <Alert variant="error" text={error} />}
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between">
        <Button size="medium" schema="tertiary" onClick={onClose} disabled={!onClose}>
          Close
        </Button>
        <Button
          size="medium"
          onClick={() => actions.logCall().then(() => actions.setCallParams())}
          disabled={!DISABLED_CALL_STATES.includes(callState) || !callOutcome}
        >
          Save & Close
        </Button>
      </Modal.Footer>

      {loading && <Loading />}

      <RecordConfirmationModal
        show={showConsentModal}
        closeModal={() => {
          actions.startRecording({ recordingConsent: false, isDismissed: true });
          setShowConsentModal(false);
        }}
        onClickRecord={() => {
          actions.startRecording({ recordingConsent: true, isDismissed: false });
          setShowConsentModal(false);
        }}
      />
    </Modal>
  );
};

export default SingleDialerModal;
